import { useState, useEffect, useMemo, useCallback, useRef, useContext } from "react";
import {
  Box,
  Button,
  Flex,
  Grid,
  GridItem,
  IconButton,
  Text,
  useColorModeValue,
  useBreakpointValue,
} from "@chakra-ui/react";
import { MapPin, Briefcase, GraduationCap, ChevronLeft, ChevronRight } from "lucide-react";
import { IoMdHeart } from "react-icons/io";
import { useTranslation } from "react-i18next";
import { AddLike } from "../services/backend";
import { GlobalContext } from "../store/global_context";
import { useMatchCelebration } from "../context/MatchCelebrationContext";
import {
  collectExplorePhotoUrls,
  profileHasDisplayablePhoto,
} from "../util/profileQuality";
import { isUserPremium } from "../util/premiumAccess";
import { FREE_DAILY_LIKE_LIMIT, isLikeLimitError } from "../util/likeLimit";
import ProfileDetailsInterestBadge from "./ui/ProfileDetailsInterestbadge";
import CountryFlag from "./ui/CountryFlag";
import ProfilesLoadingRadar from "./ProfilesLoadingRadar";
import ReportBlockMenu from "./ReportBlockMenu";
import * as motion from "motion/react-client";
import { AnimatePresence } from "motion/react";
import { displayFirstName } from "../util/displayName";
import PremiumVerifiedBadge from "./ui/PremiumVerifiedBadge";
import { displayLifestyleValue } from "../util/lifestyleDefaults";
import {
  MOBILE_BOTTOM_NAV_TOTAL_HEIGHT,
  MOBILE_ACTION_BAR_HEIGHT,
  MOBILE_EXPLORE_ACTION_DOCK_HEIGHT,
  MOBILE_EXPLORE_PHOTO_HEIGHT,
} from "../constants/mobileApp";

function collectCandidatePhotoUrls(profile) {
  return collectExplorePhotoUrls(profile);
}

function ProfilePhoto({ src, alt, onLoad, onError }) {
  const imgRef = useRef(null);

  const reportLoad = (img) => {
    if (!img) return;
    if (img.naturalWidth > 0 && img.naturalHeight > 0) onLoad?.();
    else onError?.();
  };

  // Cached images may be complete before React attaches onLoad.
  useEffect(() => {
    const img = imgRef.current;
    if (!img || !src) return;
    if (img.complete) reportLoad(img);
  }, [src, onLoad, onError]);

  return (
    <img
      ref={imgRef}
      src={src}
      alt={alt || ""}
      onLoad={(event) => reportLoad(event.currentTarget)}
      onError={onError}
      style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
    />
  );
}

export default function ExploreProfileView({
  profile,
  onSkip,
  onLikeAdvance,
  onUnusableProfile,
  openPaywall,
  stripeSubscriptionStatus,
  onBlocked,
}) {
  const { t } = useTranslation();
  const globalCtx = useContext(GlobalContext);
  const { celebrateFromLikeResponse } = useMatchCelebration();
  const loggedInUser = JSON.parse(localStorage.getItem("user"));
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isActing, setIsActing] = useState(false);
  const [failedUrls, setFailedUrls] = useState(() => new Set());
  const [photoReady, setPhotoReady] = useState(false);
  /** Buttons stay inert until photo is ready and ghost-tap window passes. */
  const [actionsEnabled, setActionsEnabled] = useState(false);
  const skippedBrokenRef = useRef(null);
  const likedProfileIdRef = useRef(null);
  const actingRef = useRef(false);
  /** Blocks clicks after card change / action (mobile ghost clicks land ~300ms later). */
  const ignoreClicksUntilRef = useRef(0);

  const user = profile?.user ?? {};
  const candidateUrls = useMemo(() => collectCandidatePhotoUrls(profile), [profile]);
  const workingUrls = useMemo(
    () => candidateUrls.filter((url) => !failedUrls.has(url)),
    [candidateUrls, failedUrls]
  );
  const titleColor = useColorModeValue("gray.900", "white");
  const mutedColor = useColorModeValue("gray.500", "whiteAlpha.600");
  const bodyColor = useColorModeValue("gray.700", "whiteAlpha.800");
  const sectionDivider = useColorModeValue("rgba(15,23,42,0.08)", "rgba(255,255,255,0.08)");
  const ctaShadow = useColorModeValue(
    "0 4px 16px rgba(52,211,153,0.22)",
    "0 6px 22px rgba(52,211,153,0.28), 0 2px 8px rgba(253,224,71,0.08)"
  );
  const stickyBarBg = useColorModeValue("rgba(255,255,255,0.96)", "rgba(14,14,16,0.96)");
  const actionDockBorder = useColorModeValue("rgba(15,23,42,0.1)", "rgba(255,255,255,0.1)");
  const nextBtnBg = useColorModeValue("white", "rgba(255,255,255,0.06)");
  const backBtnBorder = useColorModeValue("gray.300", "whiteAlpha.200");
  const backBtnHoverBg = useColorModeValue("gray.100", "whiteAlpha.100");
  const backBtnColor = useColorModeValue("gray.800", "whiteAlpha.900");
  const isMobile = useBreakpointValue({ base: true, lg: false }, { ssr: false }) ?? false;

  const markUnusable = useCallback(() => {
    if (!user.id || skippedBrokenRef.current === user.id) return;
    skippedBrokenRef.current = user.id;
    onUnusableProfile?.();
  }, [onUnusableProfile, user.id]);

  useEffect(() => {
    setCurrentImageIndex(0);
    setFailedUrls(new Set());
    setPhotoReady(false);
    setActionsEnabled(false);
    setIsActing(false);
    actingRef.current = false;
    likedProfileIdRef.current = null;
    skippedBrokenRef.current = null;
    // Swallow the delayed mobile "ghost click" that targets the next card's buttons.
    ignoreClicksUntilRef.current = Date.now() + 650;
  }, [user.id]);

  // Unlock Next after settle even if the photo is still loading — never trap the user.
  useEffect(() => {
    if (!user.id) {
      setActionsEnabled(false);
      return undefined;
    }
    setActionsEnabled(false);
    const wait = Math.max(0, ignoreClicksUntilRef.current - Date.now());
    const timer = window.setTimeout(() => setActionsEnabled(true), wait);
    return () => window.clearTimeout(timer);
  }, [user.id]);

  useEffect(() => {
    if (!profileHasDisplayablePhoto(profile) || candidateUrls.length === 0) {
      markUnusable();
    }
  }, [profile, candidateUrls.length, markUnusable]);

  useEffect(() => {
    if (candidateUrls.length > 0 && workingUrls.length === 0) {
      markUnusable();
    }
  }, [candidateUrls.length, workingUrls.length, markUnusable]);

  useEffect(() => {
    if (workingUrls.length === 0) return;
    if (currentImageIndex >= workingUrls.length) {
      setCurrentImageIndex(0);
      setPhotoReady(false);
    }
  }, [workingUrls.length, currentImageIndex]);

  const photoSrc = workingUrls[Math.min(currentImageIndex, Math.max(workingUrls.length - 1, 0))] ?? null;

  const handlePhotoError = useCallback(() => {
    if (!photoSrc) {
      markUnusable();
      return;
    }
    setPhotoReady(false);
    setFailedUrls((prev) => {
      const next = new Set(prev);
      next.add(photoSrc);
      return next;
    });
  }, [photoSrc, markUnusable]);

  const handlePhotoLoad = useCallback(() => {
    setPhotoReady(true);
  }, []);

  // Hung / black images often never fire onError — fail the URL so we can skip.
  useEffect(() => {
    if (!photoSrc || photoReady) return undefined;
    const timer = window.setTimeout(() => {
      handlePhotoError();
    }, 2500);
    return () => window.clearTimeout(timer);
  }, [photoSrc, photoReady, handlePhotoError]);

  // Brief loader while we auto-skip a broken card — never leave a blank page.
  if (!user.id || !profileHasDisplayablePhoto(profile) || !photoSrc || workingUrls.length === 0) {
    return (
      <Box w="100%" flex={{ base: 1, lg: "unset" }} minH={{ base: "240px", lg: "56vh" }} display="flex">
        <ProfilesLoadingRadar minH={{ base: "100%", lg: "56vh" }} />
      </Box>
    );
  }

  const distanceKm =
    profile?.distance != null && Number.isFinite(Number(profile.distance))
      ? Math.trunc(Number(profile.distance))
      : null;
  const countryLabel = typeof user.country === "string" ? user.country.trim() : "";
  const shortFirstName = displayFirstName(user.firstName);

  const lifestyleItems = [
    [t("profile.labelDrinking"), displayLifestyleValue(user.drinking)],
    [t("profile.labelSmoking"), displayLifestyleValue(user.smoking)],
    [t("profile.labelWorkout"), displayLifestyleValue(user.workout)],
    [t("profile.labelReligion"), displayLifestyleValue(user.religion)],
  ];

  const nextImage = () => {
    if (workingUrls.length <= 1) return;
    setPhotoReady(false);
    setCurrentImageIndex((prev) => (prev === workingUrls.length - 1 ? 0 : prev + 1));
  };

  const prevImage = () => {
    if (workingUrls.length <= 1) return;
    setPhotoReady(false);
    setCurrentImageIndex((prev) => (prev === 0 ? workingUrls.length - 1 : prev - 1));
  };

  function handleSkip() {
    if (actingRef.current || isActing || !actionsEnabled) return;
    if (Date.now() < ignoreClicksUntilRef.current) return;
    actingRef.current = true;
    setIsActing(true);
    setActionsEnabled(false);
    // Next card mounts under the same tap coordinates — block its ghost click.
    ignoreClicksUntilRef.current = Date.now() + 650;
    try {
      onSkip?.();
    } finally {
      window.setTimeout(() => {
        actingRef.current = false;
        setIsActing(false);
      }, 300);
    }
  }

  async function handleLike() {
    if (actingRef.current || isActing || !actionsEnabled || !photoReady) return;
    if (Date.now() < ignoreClicksUntilRef.current) return;
    if (!user?.id || likedProfileIdRef.current === user.id) return;
    if (!loggedInUser?.id) return;

    if (globalCtx.likeCount >= FREE_DAILY_LIKE_LIMIT && !stripeSubscriptionStatus) {
      openPaywall?.();
      return;
    }

    actingRef.current = true;
    likedProfileIdRef.current = user.id;
    setIsActing(true);
    setActionsEnabled(false);
    ignoreClicksUntilRef.current = Date.now() + 650;
    try {
      const likeResponse = await AddLike({
        LikerProfileId: loggedInUser.id,
        LikedProfileId: user.id,
      });
      await celebrateFromLikeResponse(likeResponse, user);
      if (!isUserPremium(loggedInUser)) {
        globalCtx.incrementLikeCount();
      }
      onLikeAdvance?.();
    } catch (error) {
      console.error("Like failed:", error);
      likedProfileIdRef.current = null;
      if (isLikeLimitError(error)) {
        globalCtx.updateLikeCount(FREE_DAILY_LIKE_LIMIT);
        openPaywall?.();
      }
      setActionsEnabled(true);
    } finally {
      actingRef.current = false;
      setIsActing(false);
    }
  }

  const nextBusy = isActing || !actionsEnabled;
  const likeBusy = nextBusy || !photoReady;
  const inertSx = (busy) => ({
    pointerEvents: busy ? "none" : "auto",
    opacity: busy ? 0.55 : 1,
    "& > *": { pointerEvents: "none" },
  });

  const nextButton = (
    <Button
      type="button"
      onClick={handleSkip}
      isLoading={isActing}
      isDisabled={isActing}
      size="md"
      h="40px"
      px={6}
      fontWeight="semibold"
      borderRadius="full"
      variant="outline"
      borderColor={backBtnBorder}
      color={backBtnColor}
      _hover={{ bg: backBtnHoverBg }}
      flexShrink={0}
      sx={inertSx(nextBusy)}
    >
      {t("shop.next")}
    </Button>
  );

  const likeButton = (
    <Button
      type="button"
      onClick={handleLike}
      isLoading={isActing}
      isDisabled={isActing}
      size="md"
      h="40px"
      px={6}
      fontWeight="semibold"
      borderRadius="full"
      bgGradient="linear(to-r, brand.300, brand.500)"
      color="gray.900"
      boxShadow={ctaShadow}
      _hover={{
        bgGradient: "linear(to-r, brand.400, brand.600)",
        transform: "translateY(-1px)",
      }}
      _active={{ transform: "translateY(0)" }}
      transition="all 0.2s"
      flexShrink={0}
      leftIcon={<IoMdHeart />}
      sx={inertSx(likeBusy)}
    >
      {t("shop.like")}
    </Button>
  );

  const photoCard = (
    <Box
      position="relative"
      w="100%"
      h={{ base: "100%", lg: "auto" }}
      minH={{ base: "100%", lg: "auto" }}
      mx="auto"
      bg="black"
      borderRadius={{ base: "xl", lg: "2xl" }}
      overflow="hidden"
      boxShadow={{ base: "0 12px 40px rgba(0,0,0,0.35)", lg: "lg" }}
      sx={{ aspectRatio: isMobile ? undefined : "3 / 4" }}
    >
      <Box position="absolute" inset={0}>
        <ProfilePhoto
          key={photoSrc}
          src={photoSrc}
          alt=""
          onLoad={handlePhotoLoad}
          onError={handlePhotoError}
        />

        <Box
          position="absolute"
          inset={0}
          bgGradient="linear(to-t, rgba(0,0,0,0.65), transparent 45%)"
          pointerEvents="none"
        />

        {workingUrls.length > 1 && (
          <Flex position="absolute" top={3} left={3} right={3} gap={1} zIndex={2}>
            {workingUrls.map((url, index) => (
              <Box
                key={`${url}-${index}`}
                flex={1}
                h="3px"
                borderRadius="full"
                bg={index === currentImageIndex ? "white" : "whiteAlpha.400"}
                transition="all 0.3s"
              />
            ))}
          </Flex>
        )}

        <Box position="absolute" top={workingUrls.length > 1 ? 8 : 3} right={3} zIndex={3}>
          <ReportBlockMenu
            viewerId={loggedInUser?.id}
            targetUserId={user.id}
            targetName={shortFirstName}
            onBlocked={(id) => {
              onBlocked?.(id);
              onSkip?.();
            }}
          />
        </Box>

        {workingUrls.length > 1 && (
          <>
            <IconButton
              aria-label={t("profile.prevPhoto")}
              icon={<ChevronLeft size={16} />}
              position="absolute"
              left={3}
              top="50%"
              transform="translateY(-50%)"
              size="sm"
              borderRadius="full"
              bg="blackAlpha.400"
              color="white"
              _hover={{ bg: "blackAlpha.600" }}
              onClick={prevImage}
              zIndex={2}
            />
            <IconButton
              aria-label={t("profile.nextPhoto")}
              icon={<ChevronRight size={16} />}
              position="absolute"
              right={3}
              top="50%"
              transform="translateY(-50%)"
              size="sm"
              borderRadius="full"
              bg="blackAlpha.400"
              color="white"
              _hover={{ bg: "blackAlpha.600" }}
              onClick={nextImage}
              zIndex={2}
            />
          </>
        )}

        <Box position="absolute" bottom={0} left={0} right={0} p={4} color="white" zIndex={2}>
          <Flex align="baseline" gap={1.5} minW={0} maxW="100%">
            <Text
              fontSize="2xl"
              fontWeight="bold"
              lineHeight="short"
              noOfLines={1}
              minW={0}
              title={user.firstName || undefined}
            >
              {shortFirstName}
            </Text>
            {user.age != null && user.age !== "" ? (
              <Text fontSize="2xl" fontWeight="bold" lineHeight="short" flexShrink={0} opacity={0.95}>
                , {user.age}
              </Text>
            ) : null}
            <PremiumVerifiedBadge user={user} size={20} />
          </Flex>
          {countryLabel ? (
            <Flex align="center" gap={1.5} mt={1}>
              <MapPin size={12} />
              <Text fontSize="xs" m={0} textTransform="capitalize">
                {countryLabel.toLowerCase()}
              </Text>
              <CountryFlag country={countryLabel} size={11} />
            </Flex>
          ) : null}
          {user.occupation && (
            <Flex align="center" gap={1.5} mt={1}>
              <Briefcase size={12} />
              <Text fontSize="xs" m={0}>
                {user.occupation}
              </Text>
            </Flex>
          )}
          {user.education && (
            <Flex align="center" gap={1.5} mt={1}>
              <GraduationCap size={12} />
              <Text fontSize="xs" m={0}>
                {user.education}
              </Text>
            </Flex>
          )}
          {distanceKm != null && (
            <Text fontSize="xs" fontStyle="italic" color="whiteAlpha.800" mt={1} m={0}>
              {t("shop.distanceAway", { km: distanceKm })}
            </Text>
          )}
        </Box>
      </Box>
    </Box>
  );

  const profileDetails = (
    <Box w="100%" px={{ base: 1, md: 2 }}>
      <Flex justify="space-between" align="flex-start" gap={4}>
        <Box flex="1" minW={0} display={{ base: "none", lg: "block" }}>
          <Flex align="center" gap={2} flexWrap="nowrap" minW={0} maxW="100%">
            <Text
              as="h1"
              fontSize={{ base: "3xl", md: "4xl" }}
              fontWeight="semibold"
              color={titleColor}
              letterSpacing="-0.03em"
              lineHeight="1"
              noOfLines={1}
              minW={0}
              title={user.firstName || undefined}
            >
              {shortFirstName}
            </Text>
            {user.age != null && user.age !== "" ? (
              <Text as="span" color={mutedColor} fontWeight="400" flexShrink={0} fontSize={{ base: "3xl", md: "4xl" }} lineHeight="1">
                {user.age}
              </Text>
            ) : null}
            <PremiumVerifiedBadge user={user} size={22} />
          </Flex>
          {countryLabel ? (
            <Flex mt={2} align="center" gap={2}>
              <Text fontSize="sm" color={mutedColor} textTransform="capitalize" m={0}>
                {countryLabel.toLowerCase()}
              </Text>
              <CountryFlag country={countryLabel} size={16} />
            </Flex>
          ) : null}
        </Box>
        <Flex gap={2} display={{ base: "none", lg: "flex" }} flexShrink={0}>
          {likeButton}
          {nextButton}
        </Flex>
      </Flex>

      <Box mt={{ base: 4, lg: 8 }}>
        <Text
          fontSize="xs"
          fontWeight="semibold"
          textTransform="uppercase"
          letterSpacing="0.1em"
          color={mutedColor}
          mb={2}
        >
          {t("profile.about")}
        </Text>
        <Text fontSize="sm" lineHeight="tall" color={bodyColor}>
          {user.bio || t("profile.noBio")}
        </Text>
      </Box>

      {lifestyleItems.length > 0 && (
        <Box mt={{ base: 5, lg: 8 }}>
          <Text
            fontSize="xs"
            fontWeight="semibold"
            textTransform="uppercase"
            letterSpacing="0.1em"
            color={mutedColor}
            mb={2}
          >
            {t("profile.lifestyleHeading")}
          </Text>
          <Box>
            {lifestyleItems.map(([label, value]) => (
              <Flex
                key={label}
                align="center"
                justify="space-between"
                gap={4}
                py={3}
                borderBottomWidth="1px"
                borderBottomColor={sectionDivider}
                fontSize="sm"
              >
                <Text color={mutedColor}>{label}</Text>
                <Text fontWeight="medium" color={titleColor} textAlign="right" noOfLines={1}>
                  {t(`getStarted.lifestyleOptions.${value}`, { defaultValue: value })}
                </Text>
              </Flex>
            ))}
          </Box>
        </Box>
      )}

      {user.interests?.length > 0 && (
        <Box mt={{ base: 5, lg: 8 }}>
          <Text
            fontSize="xs"
            fontWeight="semibold"
            textTransform="uppercase"
            letterSpacing="0.1em"
            color={mutedColor}
            mb={3}
          >
            {t("profile.interestsHeading")}
          </Text>
          <Flex gap={1.5} flexWrap="wrap">
            {user.interests.map((interest) => (
              <ProfileDetailsInterestBadge key={interest.interestName ?? interest.name} interest={interest} />
            ))}
          </Flex>
        </Box>
      )}
    </Box>
  );

  return (
    <AnimatePresence mode="wait">
      <motion.div
        key={user.id}
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -12 }}
        transition={{ duration: 0.22 }}
        style={{ width: "100%", display: "flex", flexDirection: "column", flex: 1, minHeight: 0 }}
      >
        <Box
          w="100%"
          flex={{ base: 1, lg: "unset" }}
          minH={{ base: 0, lg: "auto" }}
          display={{ base: "flex", lg: "block" }}
          flexDirection={{ base: "column", lg: "row" }}
          pb={{ base: 0, lg: 4 }}
        >
          {isMobile ? (
            <>
              <Box
                flexShrink={0}
                minH={MOBILE_EXPLORE_PHOTO_HEIGHT}
                h={MOBILE_EXPLORE_PHOTO_HEIGHT}
                minW={0}
                display="flex"
                flexDirection="column"
              >
                {photoCard}
              </Box>
              <Box flexShrink={0} pt={4} pb={2}>
                {profileDetails}
              </Box>
            </>
          ) : (
            <Grid
              templateColumns="repeat(5, minmax(0, 1fr))"
              gap={6}
              w="100%"
              maxW="1200px"
              mx="auto"
            >
              <GridItem colSpan={2}>
                <Box maxW="md" mx="auto">
                  {photoCard}
                </Box>
              </GridItem>
              <GridItem colSpan={3}>
                {profileDetails}
              </GridItem>
            </Grid>
          )}
        </Box>

        <Box
          display={{ base: "block", lg: "none" }}
          position="fixed"
          left={0}
          right={0}
          zIndex={1090}
          bg={stickyBarBg}
          borderTopWidth="1px"
          borderTopColor={actionDockBorder}
          borderBottomWidth="1px"
          borderBottomColor={actionDockBorder}
          backdropFilter="blur(16px) saturate(160%)"
          sx={{
            WebkitBackdropFilter: "blur(16px) saturate(160%)",
            bottom: MOBILE_BOTTOM_NAV_TOTAL_HEIGHT,
            minH: MOBILE_EXPLORE_ACTION_DOCK_HEIGHT,
          }}
        >
          <Flex align="center" gap={2} px={3} py={2} minH={MOBILE_EXPLORE_ACTION_DOCK_HEIGHT}>
            <Button
              type="button"
              onClick={handleLike}
              isLoading={isActing}
              isDisabled={isActing}
              flex="1"
              h={MOBILE_ACTION_BAR_HEIGHT}
              fontSize="sm"
              fontWeight="semibold"
              borderRadius="full"
              bgGradient="linear(to-r, brand.300, brand.500)"
              color="gray.900"
              boxShadow={ctaShadow}
              _hover={{ bgGradient: "linear(to-r, brand.400, brand.600)" }}
              _active={{ transform: "scale(0.98)" }}
              leftIcon={<IoMdHeart size={16} />}
              sx={inertSx(likeBusy)}
            >
              {t("shop.like")}
            </Button>
            <Button
              type="button"
              onClick={handleSkip}
              isLoading={isActing}
              isDisabled={isActing}
              flex="1"
              h={MOBILE_ACTION_BAR_HEIGHT}
              fontSize="sm"
              fontWeight="semibold"
              borderRadius="full"
              variant="outline"
              borderColor={backBtnBorder}
              color={backBtnColor}
              bg={nextBtnBg}
              _hover={{ bg: backBtnHoverBg }}
              _active={{ transform: "scale(0.98)" }}
              sx={inertSx(nextBusy)}
            >
              {t("shop.next")}
            </Button>
          </Flex>
        </Box>
      </motion.div>
    </AnimatePresence>
  );
}
